import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { VesselService } from '@service/vessel.service';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { ElementSettings } from 'icreate/common/models/editor-viewer-element';
import * as _ from 'lodash';
import { ElementSettingsService } from '@service/canvas/element-settings.service';
import { SelectedElementsService } from '@service/canvas/selected-elements.service';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { ElementAttribute } from '@models/editor';
import { Library } from 'icreate/common/models/library';
import { Vessel } from 'icreate/common/models/vessel';
import { MatDialog } from '@angular/material/dialog';
import { EditorMatrixDialogComponent } from '@components/editor-matrix/editor-matrix-dialog.component';
import { IntegrationDialogComponent } from '../integration-dialog/integration-dialog.component';
import { ElementIntegrationService } from '@service/element-integration.service';
import {
    ElementIntegration,
    ElementIntegrationViewData,
    INTEGRATION_TYPE,
} from 'icreate/common/models/element-integration';
import { TriggerConditionDialogComponent } from '../trigger-condition-dialog/trigger-condition-dialog.component';
import { TriggerConditionLogicDialogComponent } from '../trigger-condition-logic-dialog/trigger-condition-logic-dialog.component';
import { ElementIntegrationSocketService } from '@service/element-integration-socket.service';
import { ElementConditionSocketService } from '@service/element-condition-socket.service';
import { ElementConditionViewData } from 'icreate/common/models/element-condition';
import { MessageDialogService } from '@service/message-dialog.service';
import { CanvasService } from '@service/canvas/canvas.service';
import { ElementPositionHandleHelperService } from '@service/canvas/element-position-handle-helper.service';
import { LibraryComponentHelperService } from '@service/canvas/library-component-helper.service';
import { ValveObjectSettingComponent } from './valve-object-setting/valve-object-setting.component';
import { AutosavePage } from '@service/auto-save-controller.service';
import { DefaultElementSettingsComponent } from './default-element-settings/default-element-settings.component';
import { ControlElementSettingsComponent } from './control-element-settings/control-element-settings.component';
import { MotorObjectSettingComponent } from './motor-object-setting/motor-object-setting.component';
import { SystemColorService } from '@service/system-color.service';
import { SystemColor } from 'icreate/common/models/system-color';
import { SystemColorSocketService } from '@service/system-color-socket.service';

@Component({
    selector: 'app-settings-side-menu',
    templateUrl: './settings-side-menu.component.html',
    styleUrls: ['./settings-side-menu.component.css'],
    standalone: false,
})
export class SettingsSideMenuComponent implements OnInit, OnChanges, OnDestroy {
    readonly STATUS_COLOR = INTEGRATION_TYPE.STATUS_COLOR;
    readonly VISUAL_STATE = INTEGRATION_TYPE.VISUAL_STATE;

    readonly CONDITION_DIALOG_RELATIVE_TOP_POSITION = -12;
    readonly CONDITION_DIALOG_RELATIVE_LEFT_POSITION = -465;

    readonly CONDITION_LOGIC_DIALOG_RELATIVE_TOP_POSITION = -80;
    readonly CONDITION_LOGIC_DIALOG_RELATIVE_LEFT_POSITION = -540;

    @Input() selectedElement;
    @Input() mimicId: string;
    @Input() userCanEdit: boolean = true;
    @Input() libraryList: Library[];

    @ViewChild('deleteDialog') deleteDialog: ConfirmationWindowComponent;
    @ViewChild('valveObjectSetting') valveObjectSetting: ValveObjectSettingComponent;
    @ViewChild('defaultElemSetting') defaultElemSetting: DefaultElementSettingsComponent;
    @ViewChild('controlElemSetting') controlElemSetting: ControlElementSettingsComponent;
    @ViewChild('motorObjectSetting') motorObjectSetting: MotorObjectSettingComponent;

    vessel: Vessel;
    shipyardId: string;
    systemColors: SystemColor[];

    isLoadingChannel: boolean = false;
    elementSettings: ElementSettings;
    isOpenAddLibraryContainer: boolean = false;

    elementIntegration: ElementIntegrationViewData = undefined;
    isOpeningTriggerConditionDialog: boolean = false;
    isOpeningTriggerConditionLogicDialog: boolean = false;

    prevElement: any;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private vesselService: VesselService,
        private cdr: ChangeDetectorRef,
        private elementSettingsService: ElementSettingsService,
        private selectionService: SelectedElementsService,
        private dialog: MatDialog,
        private elementIntegrationService: ElementIntegrationService,
        private elementIntegrationSocketService: ElementIntegrationSocketService,
        private elementConditionSocketSerivce: ElementConditionSocketService,
        private messageDialogService: MessageDialogService,
        private canvasService: CanvasService,
        private elementPositionHandleHelperService: ElementPositionHandleHelperService,
        private libraryComponentHelperService: LibraryComponentHelperService,
        private systemColorService: SystemColorService,
        private systemColorSocketService: SystemColorSocketService
    ) {}

    async ngOnInit() {
        const activeVessel = this.vesselService.getActiveVessel();
        this.vessel = activeVessel;
        if (this.vessel?.shipyardId) {
            this.shipyardId = this.vessel.shipyardId;
        }

        this.elementConditionSocketSerivce
            .elementConditionUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((event: { conditionList: ElementConditionViewData[]; elementId: string }) => {
                const { conditionList, elementId } = event;

                if (!this.elementIntegration?.conditionId) return;
                if (this.getSelectedElementId() === elementId) {
                    const matchedCondition = conditionList.find(
                        condition => this.elementIntegration?.conditionId === condition.id
                    );

                    if (!matchedCondition) {
                        this.elementIntegration = {
                            ...this.elementIntegration,
                            conditionId: null,
                            elementCondition: undefined,
                        };
                    } else {
                        this.elementIntegration = {
                            ...this.elementIntegration,
                            conditionId: matchedCondition.id,
                            elementCondition: matchedCondition,
                        };
                    }
                }
            });

        this.elementIntegrationSocketService
            .elementIntegrationCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newIntegration: ElementIntegration) => {
                if (this.getSelectedElementId() === newIntegration.elementId) {
                    this.elementIntegration = {
                        ...newIntegration,
                        elementCondition: undefined,
                    };
                }
            });

        this.elementIntegrationSocketService
            .elementIntegrationUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedIntegration: ElementIntegrationViewData) => {
                if (this.getSelectedElementId() === updatedIntegration.elementId) {
                    this.elementIntegration = {
                        ...updatedIntegration,
                    };
                }
            });

        this.elementIntegrationSocketService
            .elementIntegrationDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((elementId: string) => {
                if (this.getSelectedElementId() === elementId) {
                    this.elementIntegration = undefined;
                    this.elementSettings = undefined;

                    this.showMessageDialog('integration.delete.message');
                }
            });

        this.systemColorSocketService
            .systemColorUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedSystemColor: SystemColor) => {
                if (updatedSystemColor.vesselId !== this.vessel?.id) return;
                this.systemColors = this.systemColors.map(color =>
                    color.id === updatedSystemColor.id ? updatedSystemColor : color
                );
            });

        this.systemColors = this.vessel?.id ? await this.systemColorService.getAll(this.vessel.id) : [];
    }

    async ngOnChanges(changes: SimpleChanges) {
        this.isLoadingChannel = true;
        if ((this.selectedElement && changes.selectedElement) || changes.elementIntegration) {
            const elementId = this.getSelectedElementId();
            this.elementIntegration = await this.elementIntegrationService.getByElementId(elementId);

            if (this.elementIntegration?.type === INTEGRATION_TYPE.STATUS_COLOR) {
                this.elementSettings = await this.elementSettingsService.getSettings(elementId);
            }

            this.isOpenAddLibraryContainer = false;
        }

        if (
            this.prevElement?.getAttribute(ElementAttribute.id) !==
            this.selectedElement?.getAttribute(ElementAttribute.id)
        ) {
            this.showOriginalElement();
        }
        this.cdr.detectChanges();
        this.isLoadingChannel = false;
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();

        this.showOriginalElement();
    }

    showAddLibrary() {
        const elementType = this.getElementType();
        if (
            elementType === CanvasInputMode.TRIANGLE ||
            elementType === CanvasInputMode.PATH ||
            elementType === CanvasInputMode.RECTANGLE
        )
            return true;

        return !elementType ? true : false;
    }

    openLibraryContainer() {
        this.isOpenAddLibraryContainer = true;
    }

    async clearLibraryContainer() {
        this.isOpenAddLibraryContainer = false;
    }

    getElementType() {
        return this.selectedElement?.getAttribute(ElementAttribute.type);
    }

    discardSettings() {
        if (this.isMotorElement) return this.motorObjectSetting?.motorElemSetting?.discardSettings();
        if (this.isDefaultElement) return this.defaultElemSetting?.discardSettings();
        if (this.isControlObject) return this.controlElemSetting?.discardSettings();
    }

    async saveSettings() {
        if (!this.hasChanges() || !this.selectedElement) return;
        if (this.isValveElement) return await this.valveObjectSetting?.save();
        if (this.isMotorElement) return await this.motorObjectSetting?.motorElemSetting?.save();
        if (this.isDefaultElement) return await this.defaultElemSetting?.save();
        if (this.isControlObject) return await this.controlElemSetting?.save();
    }

    hasChanges(): boolean {
        if (this.elementIntegration?.type !== INTEGRATION_TYPE.STATUS_COLOR) return false;
        if (this.isValveElement) return this.valveObjectSetting?.hasChanges() ?? false;
        if (this.isMotorElement) return this.motorObjectSetting?.motorElemSetting?.hasChanges() ?? false;
        if (this.isDefaultElement) return this.defaultElemSetting?.hasChanges() ?? false;
        if (this.isControlObject) return this.controlElemSetting?.hasChanges() ?? false;
        return false;
    }

    openMatrixDialog() {
        this.dialog.open(EditorMatrixDialogComponent, {
            data: {
                vessel: this.vessel,
                elementId: this.getSelectedElementId(),
            },
            disableClose: true,
        });
    }

    async openIntegrationDialog(integration?: ElementIntegration) {
        try {
            const dialogRef = this.dialog.open(IntegrationDialogComponent, {
                data: {
                    vessel: this.vessel,
                    mimicId: this.mimicId,
                    elementId: this.getSelectedElementId(),
                    integration: integration ? { ...integration } : undefined,
                },
                autoFocus: false,
                disableClose: true,
            });

            await lastValueFrom(dialogRef.afterClosed());
        } catch (error) {
            console.log(error);
        }
    }

    async deleteIntegration() {
        try {
            this.deleteDialog.setVisible(true, true, AutosavePage.MIMIC);

            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));

            if (option === ConfirmationWindowTypes.SAVE) {
                if (this.isValveElement) this.valveObjectSetting?.resetValveIconToDefault();
                else if (this.isDefaultElement) this.defaultElemSetting.removeAssignedChannel();
                else if (this.isControlObject) this.controlElemSetting.removeAssignedChannel();
                await this.elementIntegrationService.deleteByElementId(this.getSelectedElementId());
            }
        } catch (error) {
            console.log(error);
        }
    }

    clearElementSetting() {
        this.elementSettings = {
            ...this.elementSettings,
            channelTag: '',
            channelIsDigital: false,
            ranges: [],
            activeFlags: null,
            disabledStatus: null,
            flashingStatus: null,
            toggleState: null,
            buttonAction: null,
            elementName: null,
            tankType: null,
            selectNumber: null,
            cylinderCount: null,
            filterType: null,
            pumpType: null,
            valveSetting: null,
        };
        if (this.isValveElement) this.valveObjectSetting?.resetValveIconToDefault();
        else if (this.isDefaultElement) this.defaultElemSetting.removeAssignedChannel();
        else if (this.isControlObject) this.controlElemSetting.removeAssignedChannel();
    }

    async openTriggerConditionDialog(event: MouseEvent) {
        this.isOpeningTriggerConditionDialog = true;
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        try {
            const dialogRef = this.dialog.open(TriggerConditionDialogComponent, {
                data: {
                    vessel: this.vessel,
                    elementId: this.getSelectedElementId(),
                },
                backdropClass: 'transparent-backdrop',
                position: {
                    top: `${rect.y + this.CONDITION_DIALOG_RELATIVE_TOP_POSITION}px`,
                    left: `${rect.x + this.CONDITION_DIALOG_RELATIVE_LEFT_POSITION}px`,
                },
            });

            const selectedCondition = await lastValueFrom(dialogRef.afterClosed());

            if (selectedCondition) {
                await this.elementIntegrationService.update({
                    ...this.elementIntegration,
                    conditionId: selectedCondition.id,
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isOpeningTriggerConditionDialog = false;
        }
    }

    async openTriggerConditionLogicDialog(event: MouseEvent) {
        this.isOpeningTriggerConditionLogicDialog = true;
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        try {
            const dialogRef = this.dialog.open(TriggerConditionLogicDialogComponent, {
                data: {
                    vessel: this.vessel,
                    condition: { ...this.elementIntegration.elementCondition },
                    elementId: this.getSelectedElementId(),
                },
                backdropClass: 'transparent-backdrop',
                position: {
                    top: `${rect.y + this.CONDITION_LOGIC_DIALOG_RELATIVE_TOP_POSITION}px`,
                    left: `${rect.x + this.CONDITION_LOGIC_DIALOG_RELATIVE_LEFT_POSITION}px`,
                },
            });

            await lastValueFrom(dialogRef.afterClosed());
        } catch (error) {
            console.log(error);
        } finally {
            this.isOpeningTriggerConditionLogicDialog = false;
        }
    }

    async deleteIntegrationCondition() {
        try {
            this.isLoadingChannel = true;
            await this.elementIntegrationService.update({
                ...this.elementIntegration,
                conditionId: null,
            });
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoadingChannel = false;
        }
    }

    getSelectedElementId() {
        return this.selectedElement ? this.selectedElement.getAttribute(ElementAttribute.id) : '';
    }

    showMessageDialog(message: string, params?: Record<string, any>) {
        this.messageDialogService.openCenteredMessage(message, params, 'check');
    }

    showComponentShape() {
        if (this.prevElement) {
            this.showOriginalElement();
            return;
        }

        const svgStr = this.elementIntegration?.elementCondition?.component?.svg;
        if (!svgStr) return;

        const wrapped = `<svg xmlns="http://www.w3.org/2000/svg">${svgStr}</svg>`;

        const parsed = new DOMParser().parseFromString(wrapped, 'image/svg+xml');
        const rootSvg = parsed.documentElement as any;

        const importedRoot = document.importNode(rootSvg, true) as SVGSVGElement;

        const content = importedRoot.firstElementChild as SVGGraphicsElement | null;
        if (!content) return;

        this.prevElement = this.selectedElement.cloneNode(true);

        const selectedElementBBox = this.libraryComponentHelperService.getBBoxPosition(this.selectedElement);
        const selectedElementCx = selectedElementBBox.x + selectedElementBBox.width / 2;
        const selectedElementCy = selectedElementBBox.y + selectedElementBBox.height / 2;

        content.setAttribute('id', this.prevElement.getAttribute('id'));
        content.childNodes.forEach(element => {
            const newElementId = this.canvasService.getCanvas().getNextId();
            (element as any).setAttribute(ElementAttribute.id, newElementId);
        });

        const svgRoot = this.canvasService.getSvgRoot() as any;
        const layer = svgRoot.querySelector('.layer') as SVGGElement | null;
        if (layer) {
            layer.removeChild(this.selectedElement);
            layer.appendChild(content);
        }

        const contentBBox = this.libraryComponentHelperService.getBBoxPosition(content);

        this.elementPositionHandleHelperService.handlePositionChange(
            'X',
            selectedElementCx - contentBBox.width / 2,
            [content],
            false,
            true
        );
        this.elementPositionHandleHelperService.handlePositionChange(
            'Y',
            selectedElementCy - contentBBox.height / 2,
            [content],
            false,
            true
        );
    }

    showOriginalElement() {
        if (this.prevElement) {
            const element = document.querySelectorAll(
                `[id="${this.prevElement.getAttribute(ElementAttribute.id)}"]`
            )[0];

            const svgRoot = this.canvasService.getSvgRoot() as any;
            const layer = svgRoot.querySelector('.layer') as SVGGElement | null;
            if (layer) {
                layer.removeChild(element);
                layer.appendChild(this.prevElement);
                this.selectionService.setSelectedElements([this.prevElement]);
                this.prevElement = undefined;
            }
        }
    }

    get isPumpElement(): boolean {
        return this.selectedElement?.getAttribute(ElementAttribute.iCreateType) === CanvasInputMode.DEFAULT_PUMP;
    }

    get isFanElement(): boolean {
        return this.selectedElement?.getAttribute(ElementAttribute.iCreateType) === CanvasInputMode.DEFAULT_FAN;
    }

    get isMotorElement(): boolean {
        return this.isPumpElement || this.isFanElement;
    }

    get isValveElement(): boolean {
        return this.selectedElement?.getAttribute(ElementAttribute.iCreateType) === CanvasInputMode.DEFAULT_VALVE;
    }

    get isDefaultElement(): boolean {
        if (!this.selectedElement) return false;
        const type = this.selectedElement?.getAttribute(ElementAttribute.iCreateType);
        if (type == CanvasInputMode.GAUGE) return true;
        return !this.isControlObject && !type;
    }

    get isControlObject(): boolean {
        return this.selectedElement?.getAttribute(ElementAttribute.type);
    }
}
